/**
 * Questions Container Component
 * Single Responsibility: Manages and displays a collection of questions
 */

import React from 'react';
import {
  spacing,
  borders,
  backgroundColors,
  strokeColors,
} from '@/design-system/utils';
import { QuestionHeader } from './QuestionHeader';
import { QuestionList } from './QuestionList';

export interface QuestionsContainerProps {
  questions: string[];
  onQuestionClick?: (question: string) => void;
  title?: string;
  showTitle?: boolean;
  onClose?: () => void;
}

/**
 * Questions Container Component
 * Orchestrates the display of multiple question items
 */
export const QuestionsContainer: React.FC<QuestionsContainerProps> = ({
  questions,
  onQuestionClick,
  title = 'You may be interested to know',
  showTitle = true,
  onClose,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Prevent this element from being scrolled into view automatically
  React.useEffect(() => {
    if (containerRef.current) {
      // Prevent focus and auto-scroll
      containerRef.current.setAttribute('tabindex', '-1');
      containerRef.current.style.scrollMarginTop = '0';
    }
  }, []);

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        width: '100%',
        padding: `${spacing[3]} ${spacing[4]} ${spacing[3]} ${spacing[4]}`,
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: spacing[0.5],
        borderRadius: borders.radius.lg,
        border: `${borders.width.thin} ${borders.style.solid} ${strokeColors.base}`,
        background: backgroundColors.offset,
        boxSizing: 'border-box',
      }}
    >
      {showTitle && <QuestionHeader title={title} onClose={onClose} />}
      <QuestionList questions={questions} onQuestionClick={onQuestionClick} />
    </div>
  );
};
